import PropTypes from 'prop-types';
import { memo, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { CircularProgress } from '@material-ui/core';
import { useAnalytics } from 'use-analytics';
import Modal from '../../../../Modal';
import { SearchStatus } from '../../../../../constants';
import {
  getCheckAirAvailabilityRequest,
  updateFlightSearchObj,
} from '../../../../../store/slices/flight';
import flightSearchInput from '../../../../../helpers/selectors/flightSearchInput';
import { setFlightTripSearchInputs } from '../../../../../store/slices/tripSearch';

/**
 * A modal component that displays and allows selection of available booking classes for a flight.
 *
 * @param {string} [tripId] - The ID of the trip (optional for trip search)
 * @param {boolean} doShow - Controls the visibility of the modal
 * @param {Function} onClose - Callback when modal is closed
 */
const CheckAvailabilityModal = ({ tripId, doShow, onClose, currentIndex }) => {
  const dispatch = useDispatch();
  const { track } = useAnalytics();

  const isTripSearch = !tripId;
  const { searchInputs } = useSelector(flightSearchInput(tripId, isTripSearch));
  const segment = searchInputs?.flightSegments?.[currentIndex] || {};
  const cabinValue = segment?.cabin || '';

  /**
   * Get origin airport code, falling back to label if code not available
   */
  const originCode = useMemo(
    () => segment?.origin?.airportCode || segment?.origin?.label || '',
    [segment],
  );

  /**
   * Get destination airport code, falling back to label if code not available
   */
  const destinationCode = useMemo(
    () => segment?.destination?.airportCode || segment?.destination?.label || '',
    [segment],
  );

  /**
   * Check if we have all required inputs to make an availability request
   */
  const hasAllInputs = Boolean(
    originCode &&
    destinationCode &&
    segment?.departureDate &&
    segment?.carrierCode?.value &&
    segment?.flightNumber,
  );

  /**
   * Generate a label for the flight details display
   */
  const label = useMemo(() => {
    if (!hasAllInputs) return '';
    return `${originCode} - ${destinationCode} / ${segment.departureDate} / ${segment.carrierCode?.label} ${segment.flightNumber}`;
  }, [originCode, destinationCode, segment, hasAllInputs]);

  // Get flight state from Redux store using the label as key
  const flightState = useSelector((state) => state.flight.bookingClasses[label] || {});

  const {
    checkAirAvailabilityStatus = SearchStatus.INITIAL,
    bookingClasses = [],
    checkAirAvailabilityError = null,
  } = flightState;

  /**
   * Dispatch air availability request when modal opens
   */
  useEffect(() => {
    if (
      doShow &&
      checkAirAvailabilityStatus !== SearchStatus.SEARCHING &&
      checkAirAvailabilityStatus !== SearchStatus.DONE
    ) {
      if (!bookingClasses?.length) {
        dispatch(
          getCheckAirAvailabilityRequest({
            isTripSearch,
            tripId,
            key: label,
            currentIndex,
          }),
        );
      }
    }
  }, [
    doShow,
    dispatch,
    bookingClasses,
    isTripSearch,
    tripId,
    label,
    currentIndex,
    checkAirAvailabilityStatus,
  ]);

  /**
   * Handle cabin/booking class selection
   * Updates the selected cabin in Redux store and tracks the event
   */
  const handleCabinChange = useCallback(
    (value) => {
      // Safely create updated segments array
      const currentSegments = searchInputs?.flightSegments || [];
      const updatedSegments = [...currentSegments];

      // Update the first segment with new cabin value
      if (updatedSegments[currentIndex]) {
        updatedSegments[currentIndex] = {
          ...updatedSegments[currentIndex],
          cabin: value,
        };
      }

      const payload = {
        tripId,
        searchObj: {
          flightSegments: updatedSegments,
          page: 1,
        },
      };

      // Update Redux store based on whether this is a trip search or specific trip
      if (isTripSearch) {
        dispatch(setFlightTripSearchInputs(payload.searchObj));
      } else {
        dispatch(updateFlightSearchObj(payload));
      }

      // Track the cabin change event
      track('handleBookingClassChange.handleChange', {
        bookingClass: value,
        originCode,
        destinationCode,
        departureDate: segment?.departureDate,
        flightNumber: segment?.flightNumber,
        tripId,
      });
      if (onClose) onClose();
    },
    [
      currentIndex,
      destinationCode,
      dispatch,
      isTripSearch,
      originCode,
      searchInputs.flightSegments,
      segment.departureDate,
      segment.flightNumber,
      track,
      tripId,
      onClose,
    ],
  );

  /**
   * Render the booking class options
   */
  const renderBookingClasses = () => {
    if (!Array.isArray(bookingClasses) || bookingClasses.length === 0) {
      return <div className="text-gray-500">No availability data found</div>;
    }

    return bookingClasses.map((bookingClass, index) => {
      const availability = Number(bookingClass?.availability ?? 0);
      const bookingCode = bookingClass?.bookingClass ?? 'N/A';
      const seatText = availability === 1 ? 'seat' : 'seats';
      const isSelected = bookingCode === cabinValue;
      const isDisabled = isSelected || availability === 0;

      return (
        <div
          key={`${bookingCode}-${index.toString()}`}
          className="p-2 min-w-[140px] text-sm flex flex-col items-center justify-between gap-2"
        >
          <span>
            <span className="text-gray-900 font-semibold">{bookingCode}</span>
            {` (${availability} ${seatText})`}
          </span>

          <button
            disabled={isDisabled}
            onClick={() => handleCabinChange(bookingCode)}
            type="button"
            className={`px-2 py-1 rounded w-full ${
              isDisabled
                ? 'bg-gray-500 text-white cursor-not-allowed'
                : 'bg-button-theme text-white hover:bg-button-theme-dark'
            }`}
            aria-label={`Select ${bookingCode} booking class`}
          >
            {isSelected && availability !== 0 ? 'Added' : 'Add'}
          </button>
        </div>
      );
    });
  };

  return (
    <Modal
      open={doShow}
      onClose={onClose}
      className="w-full md:w-1/2 mx-auto overflow-hidden"
      aria-labelledby="availability-modal-title"
    >
      <div className="bg-white rounded-lg shadow-lg overflow-y-auto p-4">
        <div className="mb-4 text-lg font-semibold" id="availability-modal-title">
          <div>Booking Class Availability</div>
          {label && <div className="text-sm font-normal">{label}</div>}
        </div>

        {/* Error Message */}
        {checkAirAvailabilityError && checkAirAvailabilityStatus === SearchStatus.DONE && (
          <div className="text-red-600 text-sm bg-red-100 p-2 rounded">
            {checkAirAvailabilityError}
          </div>
        )}

        {/* Loading State */}
        {checkAirAvailabilityStatus === SearchStatus.SEARCHING ? (
          <div className="flex justify-center py-4">
            <CircularProgress color="secondary" size={25} />
          </div>
        ) : (
          /* Results Display */
          checkAirAvailabilityStatus === SearchStatus.DONE &&
          !checkAirAvailabilityError && (
            <div className="flex flex-wrap px-4 gap-6">{renderBookingClasses()}</div>
          )
        )}
      </div>
    </Modal>
  );
};

CheckAvailabilityModal.propTypes = {
  tripId: PropTypes.string,
  doShow: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  currentIndex: PropTypes.number,
};

CheckAvailabilityModal.defaultProps = {
  tripId: undefined,
  doShow: false,
  currentIndex: 0,
};

export default memo(CheckAvailabilityModal);
